import type {DataRouterApplicationBuilder} from '@github-ui/react-core/future/data-router-application'
import type {RouteErrorBoundaryFallbackProps} from '@github-ui/react-core/future/route-error-boundary'
import type {RouteObject} from '@github-ui/react-router'
import type {ComponentType} from 'react'
import {isResponseError} from '@github-ui/react-core/future/response-error'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {useEffect} from 'react'

import {buildPullRequestsLayoutRoute} from './layout-route'
import {buildPullRequestsCommitsRoute, buildPullRequestsCommitsWithRangeRoute} from './commits-route'
import {buildPullRequestsChangesRoute, buildPullRequestsChangesWithRangeRoute} from './files-changed-routes'
import {buildPullRequestsConversationsRoute} from './conversations-route'
import {buildPullRequestsChecksRoute} from './checks-route'
import {buildPullRequestsOverviewRoute} from './overview-route'
import {buildPullRequestsFindingsRoute} from './findings-route'
import {buildPullRequestsArtifactsRoute} from './artifacts-route'
import {buildRepoPullsDashboardContentRoute, REPO_PULLS_CLEAN_PATHS} from './repo-pulls-dashboard-content-route'
import {buildRepoPullsDashboardLayoutRoute} from './repo-pulls-dashboard-layout-route'
import {buildRepoStackDetailRoute} from './repo-stack-detail-route'
import {buildRepoStacksListRoute} from './repo-stacks-list-route'

interface CreatePullRequestsRoutesParams {
  isEnabled: (feature: string) => boolean | undefined
}

export interface PullRequestsRouteComponents {
  PullRequestErrorState: ComponentType
  LayoutEntrypoint: ComponentType
  CommitsEntrypoint: ComponentType
  CommitsWithRangeEntrypoint: ComponentType
  ChangesEntrypoint: ComponentType
  ChangesWithRangeEntrypoint: ComponentType
  ConversationsEntrypoint: ComponentType
  ChecksEntrypoint: ComponentType
  OverviewRouteEntrypoint: ComponentType
  FindingsEntrypoint: ComponentType
  ArtifactsEntrypoint: ComponentType
  RepoPullsDashboardContent: ComponentType
  RepoPullsDashboardLayout: ComponentType
  RepoStacksList: ComponentType
  RepoStackDetail: ComponentType
}

function ReloadPage() {
  useEffect(() => {
    ssrSafeWindow?.location.reload()
  }, [])
  return null
}

function RepoPullsCleanPathRoute() {
  return null
}

const repoPullsCleanPathRoutes: RouteObject[] = [
  {
    id: 'repoPullsDashboardContentIndexRoute',
    path: '/:owner/:repo/pulls',
    index: true,
    Component: RepoPullsCleanPathRoute,
  },
  ...REPO_PULLS_CLEAN_PATHS.map(({id, path}) => ({
    id,
    path,
    Component: RepoPullsCleanPathRoute,
  })),
]

/**
 * Builds the Pull Requests route objects for a given app builder.
 *
 * Route configs are bound to the app builder that creates them (embedded-data
 * hydration + query-key app name), so registering these routes under a different
 * app (e.g. `repo`) requires rebuilding them with that app's builder rather than
 * importing the `pull-requests`-bound singletons directly.
 *
 * `isEnabled` reads the app's server-provided feature state (embedded data). The
 * New Files Changed route set is included when `react_data_router_pull_request_files`
 * is enabled for the request; without it, the condensed commits layout is used.
 * Overview selects the index route from the embedded layout preview state.
 * Create the app before invoking this function, and build its routes during registration.
 */
export function createPullRequestsRoutes(
  appBuilder: DataRouterApplicationBuilder<string>,
  {isEnabled}: CreatePullRequestsRoutesParams,
  components: PullRequestsRouteComponents,
): RouteObject[] {
  const {
    PullRequestErrorState,
    LayoutEntrypoint,
    CommitsEntrypoint,
    CommitsWithRangeEntrypoint,
    ChangesEntrypoint,
    ChangesWithRangeEntrypoint,
    ConversationsEntrypoint,
    ChecksEntrypoint,
    OverviewRouteEntrypoint,
    FindingsEntrypoint,
    ArtifactsEntrypoint,
    RepoPullsDashboardContent,
    RepoPullsDashboardLayout,
    RepoStacksList,
    RepoStackDetail,
  } = components
  // Shared managed error boundary for every PR content subroute. Does NOT update the
  // document title — only the outermost layout boundary may do that, so a broken content
  // pane never clobbers the browser tab title for the rest of the page.
  const errorBoundary = {fallback: <PullRequestErrorState />}
  // Layout boundary: owns the full page view, so it may update the document title.
  const layoutErrorBoundary = {fallback: <PullRequestErrorState />, setDocumentTitleOnResponseError: true}

  // Stack JSON is a 404 for deleted and unknown stacks alike, so reload and let Rails render the right page.
  const isMissingStack = (error: unknown) => isResponseError(error) && error.response.status === 404
  function RepoStackDetailErrorFallback({error}: RouteErrorBoundaryFallbackProps) {
    return isMissingStack(error) ? <ReloadPage /> : <PullRequestErrorState />
  }

  // in rails, this checks the overview feature state + route params to determine if it should render as the index route, no the feature flag
  const overviewAsIndexRoute = isEnabled('prs_overview_as_index_route') ?? false

  let indexRoute: RouteObject
  if (overviewAsIndexRoute) {
    indexRoute = buildPullRequestsOverviewRoute(appBuilder).toRoute({
      Component: OverviewRouteEntrypoint,
      errorBoundary,
    })
  } else {
    indexRoute = buildPullRequestsConversationsRoute(appBuilder).toRoute({
      Component: ConversationsEntrypoint,
      errorBoundary,
    })
  }

  const children: RouteObject[] = [
    indexRoute,
    buildPullRequestsChecksRoute(appBuilder).toRoute({
      Component: ChecksEntrypoint,
      errorBoundary,
    }),
    // The Rails controller enforces Findings access, while the layout feature state controls
    // whether navigation to this route is exposed in the UI.
    buildPullRequestsFindingsRoute(appBuilder).toRoute({
      Component: FindingsEntrypoint,
      errorBoundary,
    }),
    buildPullRequestsArtifactsRoute(appBuilder).toRoute({
      Component: ArtifactsEntrypoint,
      errorBoundary,
    }),
  ]

  children.push(
    buildPullRequestsCommitsRoute(appBuilder).toRoute({
      Component: CommitsEntrypoint,
      errorBoundary,
    }),
    buildPullRequestsCommitsWithRangeRoute(appBuilder).toRoute({
      Component: CommitsWithRangeEntrypoint,
      errorBoundary,
    }),
  )

  // The `react_data_router_pull_request_files` feature flag is set in the controller when the
  // user is enrolled in the New Files Changed feature preview.
  if (isEnabled('react_data_router_pull_request_files')) {
    children.push(
      buildPullRequestsChangesRoute(appBuilder).toRoute({
        Component: ChangesEntrypoint,
        errorBoundary,
      }),
      buildPullRequestsChangesWithRangeRoute(appBuilder).toRoute({
        Component: ChangesWithRangeEntrypoint,
        errorBoundary,
      }),
    )
  }

  const routes: RouteObject[] = [
    buildPullRequestsLayoutRoute(appBuilder).toRoute({
      errorBoundary: layoutErrorBoundary,
      Component: LayoutEntrypoint,
      children,
    }),
  ]

  if (isEnabled('repo_pulls_dashboard_surface')) {
    const dashboardChildren: RouteObject[] = [
      buildRepoPullsDashboardContentRoute(appBuilder).toRoute({
        Component: RepoPullsDashboardContent,
        errorBoundary,
        // These explicit leaves match Rails' clean paths without claiming every
        // future /pulls/* path. The parent owns rendering and query loader data.
        children: repoPullsCleanPathRoutes,
      }),
    ]

    // The server only enables this flag when the dashboard and stacks are both on for the repository.
    if (isEnabled('pull_requests_stacks_view_ui')) {
      dashboardChildren.push(
        buildRepoStacksListRoute(appBuilder).toRoute({Component: RepoStacksList, errorBoundary}),
        buildRepoStackDetailRoute(appBuilder).toRoute({
          Component: RepoStackDetail,
          errorBoundary: {fallback: RepoStackDetailErrorFallback, shouldReport: error => !isMissingStack(error)},
        }),
      )
    }

    routes.unshift(
      buildRepoPullsDashboardLayoutRoute(appBuilder).toRoute({
        Component: RepoPullsDashboardLayout,
        errorBoundary: layoutErrorBoundary,
        children: dashboardChildren,
      }),
    )
  }

  return routes
}
